import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { REGISTER } from '../../constants/core.constant';
import { RegistrationService } from 'src/app/shared/services/registration/registration.service';
import { ToastService } from 'src/app/shared/services/toast-service/toast.service';
import { SharedModule } from 'src/app/shared/shared.module';
import { OktaAuth } from '@okta/okta-auth-js';
import { OktaAuthStateService, OKTA_AUTH } from '@okta/okta-angular';
import { LoaderService } from 'src/app/shared/services/loader/loader.service';

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'app-register',
    imports: [CommonModule, RouterModule, ReactiveFormsModule, SharedModule],
    templateUrl: './register.component.html',
    styleUrl: './register.component.scss'
})
export class RegisterComponent implements OnInit {
  registerForm: FormGroup;
  submitted = false;
  loading: boolean = false;
  overlayState: boolean = false;
  isDisabled: boolean = false;

  registrationCon = REGISTER;
  errorMsg: string;
  @ViewChild('successTpl', { static: false }) successTpl!: TemplateRef<any>;
  @ViewChild('dangerActionTpl', { static: false }) dangerActionTpl!: TemplateRef<any>;

  constructor(
    private fb: FormBuilder,
    private registrationService: RegistrationService,
    private toastService: ToastService,
    private readonly loaderService: LoaderService,
    @Inject(OKTA_AUTH) private readonly _oktaAuth: OktaAuth,
    private readonly _oktaStateService: OktaAuthStateService,
  ) {
    this.registerForm = this.fb.group({
      email: ['', [
        Validators.required,
        Validators.email,
        Validators.pattern(/^[a-zA-Z0-9._%+-]+@(?:wbd\.com|wbdcontractor\.com)$/i)
      ]]
    });
  }

  /**
   * ngOnInit() - Lifecycle hook that is called after data-bound properties are initialized.
   */
  ngOnInit() {
    this.loaderService.loading$.subscribe((loading) => {
      this.loading = loading;
    });
    this.loaderService.overlayState$.subscribe((applying) => {
      this.overlayState = applying;
    });
  }

  /**
   * email() - Function to get the email as soon as user enters in UI.
   */
  get email() {
    return this.registerForm.get('email');
  }

  /**
   * onEmailChange() - clear error as sonn as user edits the user field
   */
  onEmailChange(): void {
    if (this.errorMsg) {
      this.errorMsg = '';
    }
  }
  
  /**
   * onSubmit() - Function to vilidate and add the user's emails to the db.
   */
  onSubmit(): void {
    this.submitted = true;

    if (this.registerForm.invalid) {
      return;
    }

    const { email } = this.registerForm.value;
    this.errorMsg = '';

    this.isDisabled = true;
    this.loaderService.setLoadingState(true);
    this.registrationService.registerCorporateUser(email).subscribe({
      next: (response) => {
        this.showSuccess(this.successTpl);
        this.loaderService.setLoadingState(false);
      },
      error: (error: string) => {
        const err = (error || '').toString().toLowerCase();

        if (err.includes('user not found')) {
          this.errorMsg = this.registrationCon.emailNotFound;
        } else if ((err.includes('already') && err.includes('exist')) || err.includes('already registered')) {
          this.errorMsg = this.registrationCon.emailExists;
        } else {
          this.errorMsg = error || 'Something went wrong during registration.';
        }
        console.error('Registration error:', error);
        this.loaderService.setLoadingState(false);
        this.isDisabled = false;
      }
    });
  }

  /**
 * showSuccess() - Displays a success toast notification with custom content and options.
 * @param successTpl - The template reference containing the toast's content.
 */
  showSuccess(successTpl: TemplateRef<any>): void {
    this.toastService.clear();
    this.toastService.show(successTpl, {
      classname: 'custom-toast',
      delay: 6000,
    });
  }

  /**
   * onSignInAccess() - Triggers the same Okta sign-in flow as the main user sign-in button.
   */
  async onSignInAccess(): Promise<void> {
    localStorage.removeItem('okta-cache-storage');
    localStorage.removeItem('okta-original-uri-storage');
    localStorage.removeItem('okta-token-storage');
    localStorage.removeItem('okta-shared-transaction-storage');
    localStorage.removeItem('logout-event');
    localStorage.removeItem('user-profile');

    try {
      await this._oktaAuth.signInWithRedirect();
    } catch (error) {
      console.error('Okta signInWithRedirect error (Sign In Access):', error);
    }
  }
}
